/**
 * DatabaseConnectionDetailsModal Component
 *
 * Modal for viewing database connection details.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useDatabaseConnectionDetails } from '../hooks/useDatabaseConnectionDetails';
import { useRotateSshKey } from '../hooks/useRotateSshKey';
import { useTestDatabaseConnection } from '../hooks/useTestDatabaseConnection';
import { formatDate } from '../../../lib/utils/format';
import { getErrorMessage } from '../../../lib/utils/errors';

interface DatabaseConnectionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  dbConnectionId: string;
}

/**
 * Get display name for database engine
 */
const getEngineDisplayName = (engine: string): string => {
  switch (engine) {
    case 'SQL_SERVER':
      return 'SQL Server';
    case 'MYSQL':
      return 'MySQL';
    case 'POSTGRESQL':
      return 'PostgreSQL';
    case 'ORACLE':
      return 'Oracle';
    default:
      return engine;
  }
};

/**
 * DatabaseConnectionDetailsModal component
 */
export const DatabaseConnectionDetailsModal: React.FC<DatabaseConnectionDetailsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  dbConnectionId,
}) => {
  const { data: dbConnection, isLoading, error } = useDatabaseConnectionDetails(tenantId, dbConnectionId);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [rotateSuccess, setRotateSuccess] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; latencyMs?: number } | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const handleClose = () => {
    setTestResult(null);
    setTestError(null);
    onClose();
  };

  const testConnection = useTestDatabaseConnection(tenantId, dbConnectionId, {
    onSuccess: (result) => {
      setTestResult(result);
      setTestError(null);
      // Clear success message after 8 seconds
      if (result.success) {
        setTimeout(() => setTestResult(null), 8000);
      }
    },
    onError: (errorMessage) => {
      setTestError(errorMessage);
      setTestResult(null);
    },
  });

  const rotateSshKey = useRotateSshKey(tenantId, dbConnectionId, {
    onSuccess: () => {
      setShowRotateConfirm(false);
      setRotateSuccess(true);
      setRotateError(null);
      // Clear success message after 5 seconds
      setTimeout(() => setRotateSuccess(false), 5000);
    },
    onError: (errorMessage) => {
      setRotateError(errorMessage);
    },
  });

  const handleRotateClick = () => {
    setShowRotateConfirm(true);
    setRotateError(null);
  };

  const handleRotateConfirm = () => {
    rotateSshKey.mutate();
  };

  const handleRotateCancel = () => {
    setShowRotateConfirm(false);
    setRotateError(null);
  };

  const isManaged = dbConnection?.connectionType === 'KETRICS_MANAGED';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>Database Connection Details</Modal.Header>
      <Modal.Body>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Database Connection Details */}
        {dbConnection && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Connection Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{dbConnection.dbConnectionName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Connection Code</dt>
                  <dd className="mt-1">
                    <CopyableCode value={dbConnection.dbConnectionCode} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Connection ID</dt>
                  <dd className="mt-1">
                    <CopyableCode value={dbConnection.dbConnectionId} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <Badge variant={dbConnection.isActive ? 'success' : 'warning'}>
                      {dbConnection.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Connection Type</dt>
                  <dd className="mt-1">
                    <Badge variant={isManaged ? 'info' : 'warning'}>
                      {isManaged ? 'Ketrics-Managed' : 'Self-Managed'}
                    </Badge>
                  </dd>
                </div>
                {dbConnection.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{dbConnection.description}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Connection Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Connection Details</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Database Engine</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {getEngineDisplayName(dbConnection.engine)}
                  </dd>
                </div>

                {isManaged ? (
                  <>
                    {dbConnection.managedSqlDbCode && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Managed Database</dt>
                        <dd className="mt-1">
                          <CopyableCode value={dbConnection.managedSqlDbCode} />
                        </dd>
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Authentication</dt>
                      <dd className="mt-1 text-sm text-gray-500 italic">
                        IAM-based authentication (managed by platform)
                      </dd>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Host</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">{dbConnection.host}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Port</dt>
                      <dd className="mt-1 text-sm text-gray-900">{dbConnection.port}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Database</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">{dbConnection.database}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Username</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">{dbConnection.username}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Password</dt>
                      <dd className="mt-1 text-sm text-gray-500 italic">
                        (encrypted, not shown)
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </div>

            {/* SSH Port Forwarding (self-managed only) */}
            {dbConnection.portForwarding && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">SSH Port Forwarding</h3>
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">SSH Host</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono">
                      {dbConnection.sshHost || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">SSH Port</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {dbConnection.sshPort || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">SSH Username</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono">
                      {dbConnection.sshUsername || '-'}
                    </dd>
                  </div>
                </dl>

                {/* SSH Key Information */}
                {dbConnection.sshPublicKey && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900">SSH Public Key</h4>
                      <div className="flex items-center gap-2">
                        {dbConnection.sshKeyFingerprint && (
                          <span className="text-xs text-gray-500 font-mono">
                            {dbConnection.sshKeyFingerprint}
                          </span>
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleRotateClick}
                          disabled={rotateSshKey.isPending}
                        >
                          {rotateSshKey.isPending ? 'Rotating...' : 'Rotate Key'}
                        </Button>
                      </div>
                    </div>

                    {/* Success message */}
                    {rotateSuccess && (
                      <Alert variant="success" className="mb-3">
                        SSH key rotated successfully. Please update your bastion server with the new public key below.
                      </Alert>
                    )}

                    {/* Error message */}
                    {rotateError && (
                      <Alert variant="error" className="mb-3">
                        {rotateError}
                      </Alert>
                    )}

                    {/* Rotation confirmation */}
                    {showRotateConfirm && (
                      <Alert variant="warning" className="mb-3">
                        <div className="flex flex-col gap-2">
                          <p className="text-sm">
                            Are you sure you want to rotate the SSH key? You will need to update the public key on your bastion server.
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={handleRotateConfirm}
                              disabled={rotateSshKey.isPending}
                            >
                              {rotateSshKey.isPending ? 'Rotating...' : 'Yes, Rotate'}
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={handleRotateCancel}
                              disabled={rotateSshKey.isPending}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </Alert>
                    )}

                    <p className="text-xs text-gray-600 mb-2">
                      Add this public key to your bastion server's <code className="bg-gray-200 px-1 rounded">~/.ssh/authorized_keys</code> file:
                    </p>
                    <CopyableCode
                      value={dbConnection.sshPublicKey}
                      className="text-xs break-all"
                    />
                    {dbConnection.sshKeyGeneratedAt && (
                      <p className="mt-2 text-xs text-gray-500">
                        Key generated: {formatDate(dbConnection.sshKeyGeneratedAt, 'MMM dd, yyyy HH:mm')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Metadata */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(dbConnection.createdAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(dbConnection.updatedAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="flex flex-col gap-3 w-full">
          {/* Test Result Alert */}
          {testResult && (
            <Alert
              variant={testResult.success ? 'success' : 'error'}
              className="text-sm"
            >
              {testResult.message}
            </Alert>
          )}

          {/* Test Error Alert */}
          {testError && (
            <Alert variant="error" className="text-sm">
              {testError}
            </Alert>
          )}

          {/* Footer Buttons */}
          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                testConnection.mutate();
                setTestResult(null);
                setTestError(null);
              }}
              disabled={testConnection.isPending}
              loading={testConnection.isPending}
            >
              {testConnection.isPending ? 'Testing...' : 'Test Connectivity'}
            </Button>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
